import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Brain, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function AIToolsPage() {
  const { user } = useAuth();
  const [message, setMessage] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(`assist-${Date.now()}`);

  const sendMessage = async () => {
    if (!message.trim()) return;
    const userMsg = message;
    setChatHistory(prev => [...prev, { role: "user", content: userMsg }]);
    setMessage("");
    setLoading(true);
    try {
      const { data } = await api.post("/ai/assistant", { message: userMsg, session_id: sessionId });
      setChatHistory(prev => [...prev, { role: "assistant", content: data.response }]);
    } catch (e) {
      const detail = e.response?.data?.detail || "AI assistant error. Please try again.";
      toast.error(detail);
      setChatHistory(prev => [...prev, { role: "assistant", content: "Sorry, I encountered an error. Please try again." }]);
    }
    setLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12 max-w-3xl mx-auto" data-testid="ai-tools-page">
        <div className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1">AI Tools</p>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>
            AI Learning Assistant
          </h1>
          <p className="text-sm text-slate-600 mt-1">Ask questions about leadership concepts, get explanations, or request practice questions.</p>
        </div>

        <Card className="border border-slate-200 rounded-none shadow-sm">
          <CardContent className="p-0">
            {/* Chat Messages */}
            <div className="h-[50vh] overflow-y-auto p-6 space-y-4">
              {chatHistory.length === 0 && (
                <div className="flex flex-col items-center justify-center h-full text-slate-400">
                  <Brain size={48} className="mb-3" />
                  <p className="text-sm">Start a conversation with the AI assistant</p>
                  <div className="mt-4 space-y-2 text-xs text-slate-500">
                    <p>Try asking:</p>
                    <p className="bg-slate-100 px-3 py-1.5">"Explain transformational leadership"</p>
                    <p className="bg-slate-100 px-3 py-1.5">"Generate 3 practice questions on strategic thinking"</p>
                    <p className="bg-slate-100 px-3 py-1.5">"What are the key traits of effective leaders?"</p>
                  </div>
                </div>
              )}
              {chatHistory.map((msg, idx) => (
                <div key={idx} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[80%] px-4 py-3 text-sm ${
                      msg.role === "user"
                        ? "bg-blue-800 text-white"
                        : "bg-slate-100 text-slate-800 border border-slate-200"
                    }`}
                    data-testid={`chat-msg-${idx}`}
                  >
                    <p className="whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-slate-100 border border-slate-200 px-4 py-3 text-sm text-slate-500 animate-pulse">
                    Thinking...
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="border-t border-slate-200 p-4 flex gap-2">
              <Textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask the AI assistant..."
                className="rounded-none flex-1 min-h-[40px] max-h-24 resize-none"
                rows={1}
                data-testid="ai-chat-input"
              />
              <Button onClick={sendMessage} disabled={loading || !message.trim()} className="bg-blue-800 text-white hover:bg-blue-900 rounded-none self-end" data-testid="ai-send-btn">
                <Sparkle size={16} />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
